import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import createNetworkingClient from 'utils/networking-client';
import Avatar from '@strava/ui/Avatar';
import Spinner from '@strava/ui/Spinner';
import { Markup } from 'interweave';

import ModalWithTabs, {
  ModalTabs,
  ModalTabList,
  ModalTab,
  ModalTabPanels,
  ModalTabPanel
} from '@strava/ui/ModalWithTabs';
import Button from '@strava/ui/Button';
import { isEmpty } from 'lodash-es';
import AvatarAndMeta from './AvatarAndMeta';
import {
  viewingAthleteProps,
  entityOwnerProps,
  commentsProps,
  segAndBestEffortAchievementsProps
} from '../../../../props/feed-prop-types';
import styles from './styles.scss';
import MentionableCommentForm from '../../MentionableCommentForm';

import { useFeedEntityContext } from '../../../../state/FeedEntityContext';
import {
  CLOSE_MODAL,
  UPDATE_KUDO_STATUS
} from '../../../../state/feed-entity-actions';

const prefix = 'templates.kudos_and_comments_modal';

const KudosCommentsAndAchievementsModal = ({
  title,
  comments,
  segAndBestEffortAchievements,
  viewingAthlete,
  entity,
  entityId,
  hasOtherAthletes,
  entityOwner,
  kudosCount,
  commentsEnabled
}) => {
  const { entityState, dispatch } = useFeedEntityContext();

  const [index, setIndex] = useState(entityState.selectedTabIndex);
  const [groupedActivityData, setGroupedActivityData] = useState({
    athletes: [],
    hasOtherAthletes
  });
  const [hover, setHover] = useState(false);
  const hasAchievements = !isEmpty(segAndBestEffortAchievements);
  const [kudoData, setKudosData] = useState({
    isOwner: false,
    kudosable: false,
    athletes: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [followRequestApprovalStatusMap, setAthleteFollowRequestMap] = useState(
    {}
  );

  const fetchKudoList = useCallback(() => {
    setIsLoading(true);
    createNetworkingClient()
      .get(`/feed/${entity.toLowerCase()}/${entityId}/kudos`)
      .then(({ data }) => {
        setKudosData(data);
        setIsLoading(false);
      });
  }, [entity, entityId]);

  useEffect(() => {
    fetchKudoList();
  }, [fetchKudoList]);

  const fetchOtherAthletes = useCallback(() => {
    setIsLoading(true);
    createNetworkingClient()
      .get(`/feed/${entity.toLowerCase()}/${entityId}/group_athletes`)
      .then(({ data }) => {
        setGroupedActivityData(data);
        setIsLoading(false);
      });
  }, [entity, entityId]);

  const toggleHover = () => {
    setHover((prev) => !prev);
  };

  const postKudo = () => {
    const viewAthleteKudoData = {
      member_type: viewingAthlete.memberType,
      url: `/athletes/${viewingAthlete.id}`,
      name: viewingAthlete.name,
      avatar_url: viewingAthlete.avatarUrl,
      id: viewingAthlete.id,
      is_following: true // viewing athlete cannot follow themself (this prevent the Follow button from showing)
    };
    createNetworkingClient()
      .post(`/feed/${entity.toLowerCase()}/${entityId}/kudo`)
      .then(() =>
        setKudosData((prev) => ({
          ...prev,
          athletes: prev.athletes.concat(viewAthleteKudoData),
          kudosable: false
        }))
      )
      .then(dispatch({ type: UPDATE_KUDO_STATUS }));
  };

  const isRecentlyFollowedAthlete = (kudoDataRow) =>
    followRequestApprovalStatusMap[kudoDataRow.id] &&
    followRequestApprovalStatusMap[kudoDataRow.id].requestApprovalStatus ===
      'approved';

  const isRecentlyRequestedAthlete = (kudoDataRow) =>
    followRequestApprovalStatusMap[kudoDataRow.id] &&
    followRequestApprovalStatusMap[kudoDataRow.id].requestApprovalStatus ===
      'pending';

  const onKudoDataRowButtonClick = (kudoDataRow) => {
    const kudoserId = kudoDataRow.id;
    const viewingAthleteId = viewingAthlete.id;
    if (
      isRecentlyRequestedAthlete(kudoDataRow) ||
      isRecentlyFollowedAthlete(kudoDataRow)
    ) {
      // Athlete reversing their follow add/request action
      createNetworkingClient()
        .delete(
          `/athletes/${viewingAthleteId}/follows/${followRequestApprovalStatusMap[kudoDataRow.id].followId}`
        )
        .then(() => {
          setAthleteFollowRequestMap((prevState) =>
            Object.assign(prevState, {
              [kudoserId]: { requestApprovalStatus: '' }
            })
          );
        })
        .then(toggleHover);
    } else {
      const payload = {
        follow: {
          following_id: kudoserId,
          follower_id: viewingAthleteId
        }
      };
      createNetworkingClient()
        .post(`/athletes/${viewingAthleteId}/follows`, payload)
        .then((response) => {
          const requestApprovalStatus = response.data.approved
            ? 'approved'
            : 'pending';
          setAthleteFollowRequestMap((prevState) =>
            Object.assign(prevState, {
              [kudoserId]: {
                requestApprovalStatus,
                followId: response.data.follow_id
              }
            })
          );
        })
        .then(toggleHover);
    }
  };

  const leaveAthleteGroup = () => {
    setIsLoading(true);
    createNetworkingClient()
      .delete(`/activities/${entityId}/grouping`)
      .then(() => {
        setGroupedActivityData({ athletes: [], hasOtherAthletes: false });
        setIsLoading(false);
      });
  };

  const processFollowButtonAtrributes = (kudoDataRow) => {
    let label;
    let variant;
    if (isRecentlyFollowedAthlete(kudoDataRow)) {
      label = hover
        ? I18n.t(`${prefix}.unfollow`)
        : I18n.t(`${prefix}.following`);
      variant = 'default';
    } else if (isRecentlyRequestedAthlete(kudoDataRow)) {
      label = hover
        ? I18n.t(`${prefix}.follow_requested`)
        : I18n.t(`${prefix}.cancel_follow_request`);
      variant = 'default';
    } else {
      label = kudoDataRow.is_private
        ? I18n.t(`${prefix}.request_to_follow`)
        : I18n.t(`${prefix}.follow`);
      variant = 'primary';
    }
    return { label, variant };
  };

  const buildFollowButton = (kudoDataRow) => {
    const followButtonAttributes = processFollowButtonAtrributes(kudoDataRow);
    return (
      <Button
        data-testid="follow-btn"
        variant={followButtonAttributes.variant}
        onClick={() => onKudoDataRowButtonClick(kudoDataRow)}
        onMouseEnter={toggleHover}
        onMouseLeave={toggleHover}
      >
        {followButtonAttributes.label}
      </Button>
    );
  };

  const spinner = (
    <div className={styles.spinner}>
      <Spinner />
    </div>
  );

  const entriesFromFetchedData = (dataList) => {
    const components = [];
    dataList.athletes.forEach((kudoDataRow) => {
      components.push(
        <div className={styles.entry} key={kudoDataRow.id} data-testid="entry">
          <AvatarAndMeta athleteMetaData={kudoDataRow} />
          {!kudoDataRow.is_following && buildFollowButton(kudoDataRow)}
        </div>
      );
    });
    return components;
  };

  const buildKudoList = () => {
    if (isLoading) {
      return spinner;
    }
    // activity owner is not allowed to kudo their own activity
    const kudoAllowed = !kudoData.isOwner && kudoData.kudosable;
    const kudoEntries = entriesFromFetchedData(kudoData);
    return (
      <>
        <div className={styles.body}>{kudoEntries}</div>
        <div className={styles.footer}>
          <Button
            disabled={!kudoAllowed}
            onClick={postKudo}
            variant={kudoAllowed ? 'primary' : 'default'}
          >
            {I18n.t(`${prefix}.give_kudos`)}
          </Button>
        </div>
      </>
    );
  };

  const buildOtherAthleteList = () => {
    if (isLoading) {
      return spinner;
    }
    const groupAthletesEntries = entriesFromFetchedData(groupedActivityData);
    return (
      <>
        <div className={styles.body}>{groupAthletesEntries}</div>
        {!isEmpty(groupedActivityData.athletes) && (
          <div className={styles.footer}>
            <Button onClick={leaveAthleteGroup} variant="primary">
              {I18n.t(`${prefix}.leave_group`)}
            </Button>
          </div>
        )}
      </>
    );
  };

  const buildCommentList = () => {
    const thread = [];
    comments.forEach((entry) => {
      thread.push(
        <div
          className={styles.entry}
          key={entry.comment_id}
          data-testid="entry"
        >
          <AvatarAndMeta
            athleteMetaData={entry.athlete}
            rowContent={entry.comment}
          />
          <div className={styles.timestamp}>
            <Markup noWrap={true} content={entry.timestamp} />
          </div>
        </div>
      );
    });
    return (
      <>
        <div className={styles.body}>{thread}</div>
        <div className={`${styles.footer} ${styles.commentsForm}`}>
          <MentionableCommentForm
            viewingAthlete={viewingAthlete}
            autoFocus={true}
            entity={entity}
            entityId={entityId}
          />
        </div>
      </>
    );
  };

  const buildAchievementList = () => '[Achievements]';

  // tab order - kudos, comments, achievements (optional), other athletes (optional)
  const onTabChange = (selectedTab) => {
    setIndex(selectedTab);
    if (selectedTab === 0) {
      // kudos tab
      fetchKudoList();
    } else if (
      (hasAchievements && selectedTab === 3) ||
      (!hasAchievements && selectedTab === 2)
    ) {
      // other athletes tab
      fetchOtherAthletes();
    }
  };

  const modalTitle = () => {
    return (
      <div className={styles.title}>
        <div className={styles.avatar}>
          <Avatar
            name={entityOwner.name}
            src={entityOwner.displayImage}
            type={entityOwner.type}
            badge={entityOwner.memberType}
            href={entityOwner.profileUrl}
            size="small"
          />
        </div>
        <div className={styles.description}>{title}</div>
      </div>
    );
  };

  const kudosTabTitle = () => {
    return I18n.t(`${prefix}.kudos`).concat(` (${kudosCount})`);
  };

  const commentsTabTitle = () => {
    return I18n.t(`${prefix}.comments`).concat(` (${comments.length})`);
  };

  const acheivementsTabTitle = () => {
    return I18n.t(`${prefix}.achievements`).concat(
      ` (${segAndBestEffortAchievements.length})`
    );
  };

  const otherAthletesTabTitle = () => {
    return I18n.t(`${prefix}.other_athletes`).concat(
      ` (${groupedActivityData.athletes.length})`
    );
  };

  return (
    <ModalWithTabs
      title={modalTitle()}
      isOpen={true}
      onDismiss={() => dispatch({ type: CLOSE_MODAL })}
    >
      <ModalTabs index={index} onChange={onTabChange} defaultIndex={0}>
        <ModalTabList>
          <ModalTab>{kudosTabTitle()}</ModalTab>
          {commentsEnabled && <ModalTab>{commentsTabTitle()}</ModalTab>}
          {hasAchievements && <ModalTab>{acheivementsTabTitle()}</ModalTab>}
          {groupedActivityData.hasOtherAthletes && (
            <ModalTab>{otherAthletesTabTitle()}</ModalTab>
          )}
        </ModalTabList>
        <ModalTabPanels>
          <ModalTabPanel>
            <div className={styles.tabContent}>{buildKudoList()}</div>
          </ModalTabPanel>
          {commentsEnabled && (
            <ModalTabPanel>
              <div className={styles.tabContent}>{buildCommentList()}</div>
            </ModalTabPanel>
          )}
          {hasAchievements && (
            <ModalTabPanel>
              <div className={styles.tabContent}>{buildAchievementList()}</div>
            </ModalTabPanel>
          )}
          {hasOtherAthletes && (
            <ModalTabPanel>
              <div className={styles.tabContent}>{buildOtherAthleteList()}</div>
            </ModalTabPanel>
          )}
        </ModalTabPanels>
      </ModalTabs>
    </ModalWithTabs>
  );
};

KudosCommentsAndAchievementsModal.propTypes = {
  title: PropTypes.string.isRequired,
  comments: commentsProps,
  segAndBestEffortAchievements: segAndBestEffortAchievementsProps,
  viewingAthlete: viewingAthleteProps.isRequired,
  entity: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  hasOtherAthletes: PropTypes.bool,
  entityOwner: entityOwnerProps.isRequired,
  kudosCount: PropTypes.number.isRequired,
  commentsEnabled: PropTypes.bool // this is currently only used by posts (AR work)
};

KudosCommentsAndAchievementsModal.defaultProps = {
  comments: [],
  segAndBestEffortAchievements: [],
  hasOtherAthletes: false,
  commentsEnabled: true
};
export default KudosCommentsAndAchievementsModal;
